import { useMemo } from "react";
import {
  FinancialApplicationFormSectionSummary,
} from "../FinancialApplicationFormSectionSummary/FinancialApplicationFormSectionSummary";
import { FIELD_KEYS, COMPLEX_FIELD_KEYS } from "@/constants";
import { useFieldSchemaToGetDataForSummary } from "../FinancialApplicationFormSectionSummary/useFieldSchemaToGetDataForSummary";
import { FieldInput, getHiddenGovernmentIdFields } from "@/utils/getFieldSchemaData";
import { useFinancialApplicationForm } from "@/hooks/useFinancialApplicationForm";
import { SUBSTEP_PATHS } from "@/config/stepPaths";
import { useFinancialApplicationCoreData } from "@/hooks/useFinancialApplicationCoreData";

export interface BasicInformationSummaryProps {
  financialApplicationId: string;
}

export const BasicInformationSummary: React.FC<BasicInformationSummaryProps> = ({
  financialApplicationId,
}) => {
  const { formValue } = useFinancialApplicationForm(financialApplicationId);

  const { conditionalFlags } = useFinancialApplicationCoreData(financialApplicationId);

  const employmentInformationFieldsToInclude: FieldInput[] = useMemo(
    () => [
      {
        fieldProps: FIELD_KEYS.FINANCIAL_APPLICATION.EMPLOYMENT_INFORMATION,
        complexFieldValue: {
          hiddenFields: !conditionalFlags.shouldShowAttorneyBarNumber
            ? [COMPLEX_FIELD_KEYS.EMPLOYMENT_INFORMATION.ATTORNEY_BAR_NUMBER]
            : [],
        },
      },
    ],
    [conditionalFlags.shouldShowAttorneyBarNumber]
  );

  const fieldsToInclude: FieldInput[] = useMemo(() => {
    const hiddenGovernmentIdFields = getHiddenGovernmentIdFields(
      formValue?.governmentIdInformation
    );

    return [
      {
        fieldProps: FIELD_KEYS.FINANCIAL_APPLICATION.PERSON_INFORMATION,
        complexFieldValue: {
          hiddenFields: [
            COMPLEX_FIELD_KEYS.PERSON_INFORMATION.PHONE_NUMBER,
            COMPLEX_FIELD_KEYS.PERSON_INFORMATION.EMAIL_ADDRESS,
            COMPLEX_FIELD_KEYS.PERSON_INFORMATION.HOME_ADDRESS,
          ],
        },
      },
      {
        fieldProps: FIELD_KEYS.FINANCIAL_APPLICATION.GOVERNMENT_ID_INFORMATION,
        complexFieldValue: {
          hiddenFields: hiddenGovernmentIdFields,
        },
      },
      {
        fieldProps: FIELD_KEYS.FINANCIAL_APPLICATION.PERSON_INFORMATION,
        complexFieldValue: {
          hiddenFields: [
            COMPLEX_FIELD_KEYS.PERSON_INFORMATION.FIRST_NAME,
            COMPLEX_FIELD_KEYS.PERSON_INFORMATION.MIDDLE_NAME,
            COMPLEX_FIELD_KEYS.PERSON_INFORMATION.LAST_NAME,
            COMPLEX_FIELD_KEYS.PERSON_INFORMATION.DATE_OF_BIRTH,
            COMPLEX_FIELD_KEYS.PERSON_INFORMATION.SOCIAL_SECURITY_NUMBER,
          ],
        },
      },
      ...employmentInformationFieldsToInclude,
      {
        fieldProps: FIELD_KEYS.FINANCIAL_APPLICATION.DIGITAL_BANKING_INFORMATION,
        complexFieldValue: {
          hiddenFields: [
            COMPLEX_FIELD_KEYS.DIGITAL_BANKING_INFORMATION.DIGITAL_BANKING_USERNAME,
            COMPLEX_FIELD_KEYS.DIGITAL_BANKING_INFORMATION.DIGITAL_BANKING_ROLE,
            COMPLEX_FIELD_KEYS.DIGITAL_BANKING_INFORMATION.DIGITAL_BANKING_EMAIL_ADDRESS,
            COMPLEX_FIELD_KEYS.DIGITAL_BANKING_INFORMATION.DIGITAL_BANKING_PHONE_NUMBER,
          ]
        },
      },
      {
        fieldProps: FIELD_KEYS.FINANCIAL_APPLICATION.SECURITY_QUESTIONS_INFORMATION,
      },
      {
        fieldProps: FIELD_KEYS.FINANCIAL_APPLICATION.DEBIT_CARD_INFORMATION,
        complexFieldValue: {
          hiddenFields: [
            COMPLEX_FIELD_KEYS.DEBIT_CARD_INFORMATION.DEBIT_CARD_NAME_DISPLAYED,
            COMPLEX_FIELD_KEYS.DEBIT_CARD_INFORMATION.BUSINESS_NAME_DISPLAYED_ON_CARD,
            COMPLEX_FIELD_KEYS.DEBIT_CARD_INFORMATION.USE_HOME_ADDRESS_FOR_DEBIT_CARD_MAILING_ADDRESS,
            COMPLEX_FIELD_KEYS.DEBIT_CARD_INFORMATION.DEBIT_CARD_MAILING_ADDRESS,
          ],
        },
      },
    ];
  }, [formValue?.governmentIdInformation]);

  const summaryData = useFieldSchemaToGetDataForSummary(financialApplicationId, fieldsToInclude);

  return (
    <FinancialApplicationFormSectionSummary
      title="Your Information"
      data={summaryData}
      navigateTo={`/step/${SUBSTEP_PATHS.AboutYou.BasicInfo}`}
    />
  );
};
